import { useEffect, useRef, useState } from 'react'
import { useRevalidator } from 'react-router'
import { type ElseDag } from '#app/utils/else-surface.server.ts'
import { tijdregel, type Tijdregel } from '#app/utils/else-tijd.ts'
import { cn } from '#app/utils/misc.tsx'
import { ELSE_EMPTY_DAY_NL } from './else-copy.ts'

/**
 * ADR-036 — the one Vandaag for Else, the same on /else and /stal/else:
 * the time line, the day in its parts with fixed times, what is over
 * stays (a ✓ only with proof, and in small text where it came from),
 * tomorrow in one sentence. Nothing counts down and nothing disappears.
 * Every string arrived through the server's `gate()`.
 */

/** Re-read the day every five minutes, and when she comes back to it. */
const VERS_MS = 5 * 60_000

/**
 * "Het is woensdagmiddag." The server's minute first, then it ticks. The
 * day under it follows: when the part of the day turns, after a while,
 * or when the page comes back into view, the page reads it again, so the
 * line and the day never disagree.
 */
function ElseTijdregel({ initial }: { initial: Tijdregel }) {
	const [t, setT] = useState(initial)
	const { revalidate } = useRevalidator()
	const kop = useRef(initial.kop)
	const gelezen = useRef(Date.now())
	useEffect(() => {
		const vers = () => {
			gelezen.current = Date.now()
			void revalidate()
		}
		const tick = () => {
			const next = tijdregel(new Date())
			setT(next)
			if (next.kop !== kop.current) {
				kop.current = next.kop
				vers()
			} else if (Date.now() - gelezen.current >= VERS_MS) vers()
		}
		const terug = () => {
			if (document.visibilityState === 'visible') tick()
		}
		tick()
		const id = window.setInterval(tick, 60_000)
		document.addEventListener('visibilitychange', terug)
		return () => {
			window.clearInterval(id)
			document.removeEventListener('visibilitychange', terug)
		}
	}, [revalidate])
	return (
		<header className="ed-tijd">
			<h1 className="ed-kop">{t.kop}</h1>
			<p className="ed-onder">{t.onder}</p>
		</header>
	)
}

export function ElseDagView({ dag }: { dag: ElseDag }) {
	return (
		<>
			<ElseTijdregel initial={dag.tijd} />
			{dag.groepen.length === 0 ? (
				<p className="ed-leeg">{ELSE_EMPTY_DAY_NL}</p>
			) : (
				dag.groepen.map((g) => (
					<section
						key={g.deel}
						className={cn('ed-deel', g.nu && 'nu')}
						aria-labelledby={`ed-${g.deel.replace(' ', '-')}`}
					>
						<h2 className="ed-deel-label" id={`ed-${g.deel.replace(' ', '-')}`}>
							{g.label}
							{g.nu ? (
								<>
									{' '}
									<span className="ed-nu">Nu</span>
								</>
							) : null}
						</h2>
						{g.leeg ? <p className="ed-rustig">{g.leeg}</p> : null}
						{g.items.map((it, i) => (
							<div
								key={i}
								className={cn('ed-item', it.past && 'past', it.done && 'done')}
							>
								<span className="ed-t">
									{it.time === 'hele dag' ? '' : it.time}
								</span>
								<div className="ed-body">
									<p className="ed-x">{it.text}</p>
									{it.sub ? <p className="ed-sub">{it.sub}</p> : null}
									{it.bron ? <p className="ed-bron">{it.bron}</p> : null}
								</div>
								{it.done ? (
									<span className="ed-check">
										<svg viewBox="0 0 24 24" aria-hidden="true">
											<path
												d="M5 12.5l4.5 4.5L19 7.5"
												fill="none"
												stroke="currentColor"
												strokeWidth="3"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										<span className="sr-only">geweest</span>
									</span>
								) : null}
							</div>
						))}
					</section>
				))
			)}
			{dag.morgen ? (
				<p className="ed-morgen">
					<strong>Morgen</strong> {dag.morgen}
				</p>
			) : null}
		</>
	)
}

/** The one call button: the same words, at the end of every Else page. */
export function ElseBel({ label, href }: { label: string; href: string }) {
	return (
		<a className="ed-bel" href={href}>
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path
					d="M6.6 3.5l2.6.4 1.3 4-1.9 1.5a12 12 0 005.9 5.9l1.5-1.9 4 1.3.4 2.6a2 2 0 01-2 2.2A16.5 16.5 0 014.4 5.5a2 2 0 012.2-2z"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinejoin="round"
				/>
			</svg>
			{label}
		</a>
	)
}
